import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import GroupsIcon from '@mui/icons-material/Groups'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  createContributor,
  fetchContributors,
  newlyCreatedContributorIDCleared,
  selectContributors,
  selectContributorsStatus,
  selectCreateContributorStatus,
  selectNewlyCreatedContributorID,
} from '../codex.slice'

dayjs.extend(relativeTime)

let ContributorsPage = (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let contributors = useAppSelector(selectContributors)
  let contributorsStatus = useAppSelector(selectContributorsStatus)
  let createContributorStatus = useAppSelector(selectCreateContributorStatus)
  let newlyCreatedContributorID = useAppSelector(
    selectNewlyCreatedContributorID,
  )

  let [search, setSearch] = useState('')

  useEffect(() => {
    dispatch(fetchContributors())
  }, [dispatch])

  useEffect(() => {
    if (newlyCreatedContributorID) {
      navigate(`/codex/contributors/${newlyCreatedContributorID}`)
      dispatch(newlyCreatedContributorIDCleared())
    }
  }, [newlyCreatedContributorID, navigate, dispatch])

  let handleNewContributor = () => {
    dispatch(createContributor({ name: 'New contributor' }))
  }

  let filteredContributors = contributors.filter((c) => {
    if (!search) return true
    let q = search.toLowerCase()
    return (
      c.name.toLowerCase().includes(q) ||
      (c.title?.toLowerCase().includes(q) ?? false) ||
      (c.credentials?.toLowerCase().includes(q) ?? false)
    )
  })

  let isCreating = createContributorStatus === 'pending'
  let isLoading = contributorsStatus === 'pending'

  return (
    <Container maxWidth="xl" className="py-6">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Contributors
          </Typography>
          {!isLoading && (
            <Typography
              variant="h5"
              color="text.disabled"
              sx={{ fontWeight: 400 }}
            >
              {contributors.length}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search contributors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: 'text.disabled' }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 240 }}
          />
          <Button
            variant="contained"
            startIcon={
              isCreating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            onClick={handleNewContributor}
            disabled={isCreating}
            sx={{ whiteSpace: 'nowrap' }}
          >
            New contributor
          </Button>
        </Box>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {!isLoading && contributors.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 12,
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.5,
            }}
          >
            <GroupsIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            No contributors yet
          </Typography>
        </Box>
      )}

      {contributors.length > 0 && filteredContributors.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No contributors match &ldquo;{search}&rdquo;.
          </Typography>
        </Box>
      )}

      {filteredContributors.length > 0 && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 200px 200px',
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              Contributor
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              Title
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
              }}
            >
              Created
            </Typography>
          </Box>

          {filteredContributors.map((contributor) => (
            <Box
              key={contributor.id}
              onClick={() => navigate(`/codex/contributors/${contributor.id}`)}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 200px 200px',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Avatar
                  src={
                    contributor.profileImage
                      ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${contributor.profileImage}`
                      : undefined
                  }
                  sx={{ width: 36, height: 36, flexShrink: 0 }}
                >
                  {contributor.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {contributor.name}
                  </Typography>
                  {contributor.credentials && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {contributor.credentials}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" noWrap>
                {contributor.title ?? '—'}
              </Typography>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  {contributor.createdTime
                    ? dayjs(contributor.createdTime).format('D MMM YYYY')
                    : '—'}
                </Typography>
                {contributor.createdTime && (
                  <Typography variant="caption" color="text.disabled">
                    {dayjs(contributor.createdTime).fromNow()}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Paper>
      )}
    </Container>
  )
}

export default ContributorsPage
